import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useState } from "react";
import {
  FolderOpenDot,
  Sparkles,
  FileStack,
  Gavel,
  ShieldAlert,
  ShieldCheck,
  LogOut,
  Crown,
  ArrowRight,
  Settings,
  MessageCircle,
  X,
} from "lucide-react";

const ITEMS = [
  {
    to: "/dashboard",
    end: true,
    label: "Mis Proyectos",
    icon: FolderOpenDot,
    testid: "sidebar-nav-projects",
  },
  {
    to: "/dashboard/writing",
    label: "Asistente de Redacción",
    icon: Sparkles,
    testid: "sidebar-nav-writing",
  },
  {
    to: "/dashboard/templates",
    label: "Plantillas Universitarias",
    icon: FileStack,
    testid: "sidebar-nav-templates",
  },
  {
    to: "/dashboard/jury",
    label: "Simulador de Jurado",
    icon: Gavel,
    testid: "sidebar-nav-jury",
  },
  {
    to: "/dashboard/security",
    label: "Detector de IA",
    icon: ShieldAlert,
    testid: "sidebar-nav-security",
  },
  {
    to: "/dashboard/blindaje",
    label: "Seguridad",
    icon: ShieldCheck,
    testid: "sidebar-nav-blindaje",
  },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const isPremium = user?.plan === "premium";

  return (
    <aside
      data-testid="dashboard-sidebar"
      className="h-full w-full lg:w-64 bg-paper-2/60 lg:bg-paper-2/40 border-r border-rule flex flex-col"
    >
      {/* Brand */}
      <div className="px-5 pt-6 pb-5 border-b border-rule">
        <Link
          to="/"
          data-testid="sidebar-brand"
          className="inline-flex items-baseline gap-0.5"
        >
          <span className="font-serif italic text-2xl text-copper">una</span>
          <span className="font-serif text-2xl text-ink">TESIS</span>
        </Link>
        <p className="mt-2 text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
          Escritorio del sustentante
        </p>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-5">
        <p className="px-3 text-[10px] tracking-[0.2em] uppercase text-ink-soft/70 font-medium mb-2">
          Espacio de trabajo
        </p>
        <ul className="flex flex-col gap-1">
          {ITEMS.map((it) => (
            <li key={it.to}>
              <NavLink
                to={it.to}
                end={it.end}
                onClick={onNavigate}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-all ${
                    isActive
                      ? "bg-white text-forest shadow-sm border border-rule"
                      : "text-ink-soft hover:bg-white/70 hover:text-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`grid place-items-center h-7 w-7 rounded-md transition-colors ${
                        isActive
                          ? "bg-forest text-paper"
                          : "bg-white border border-rule text-ink-soft group-hover:text-forest"
                      }`}
                    >
                      <it.icon size={14} />
                    </span>
                    <span className={isActive ? "font-medium" : ""}>{it.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {user?.role === "admin" && (
          <>
            <div className="my-5 h-px bg-rule" />
            <p className="px-3 text-[10px] tracking-[0.2em] uppercase text-ink-soft/70 font-medium mb-2">
              Administración
            </p>
            <NavLink
              to="/admin"
              onClick={onNavigate}
              data-testid="sidebar-nav-admin"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-ink-soft hover:bg-white/70 hover:text-ink"
            >
              <span className="grid place-items-center h-7 w-7 rounded-md bg-white border border-rule">
                <Settings size={14} />
              </span>
              Panel admin
            </NavLink>
          </>
        )}
      </nav>

      {/* Upgrade card / Premium card */}
      <div className="px-3 pb-3">
        {!isPremium ? (
          <div
            data-testid="sidebar-upgrade-card"
            className="rounded-xl border border-copper/30 bg-[#FBF6EC] p-4"
          >
            <div className="flex items-center gap-2">
              <span className="grid place-items-center h-8 w-8 rounded-full bg-forest text-paper">
                <Crown size={14} className="text-[#E0C58A]" />
              </span>
              <p className="font-serif text-base text-ink">Pasa a Premium</p>
            </div>
            <p className="mt-2 text-[11.5px] text-ink-soft leading-snug">
              Proyectos ilimitados, generaciones sin tope y plantillas oficiales.
            </p>
            <Link
              to="/pricing"
              onClick={onNavigate}
              data-testid="sidebar-upgrade-cta"
              className="mt-3 group inline-flex items-center gap-1.5 text-[12px] font-medium text-forest hover:text-forest-dark"
            >
              Ver planes
              <ArrowRight
                size={12}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        ) : (
          <div className="rounded-xl border border-forest/20 bg-white p-4">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center h-8 w-8 rounded-full bg-forest text-paper">
                <Crown size={14} className="text-[#E0C58A]" />
              </span>
              <div className="leading-tight">
                <p className="font-serif text-base text-ink">Premium activo</p>
                <p className="text-[10.5px] text-ink-soft">
                  Vigente hasta{" "}
                  {user.plan_expires_at
                    ? new Date(user.plan_expires_at).toLocaleDateString("es-MX", {
                        day: "2-digit",
                        month: "short",
                      })
                    : "—"}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Soporte técnico WhatsApp */}
      <div className="px-3 pb-3">
        <a
          href="https://wa.me/523342802102?text=Hola%20unaTESIS%2C%20necesito%20apoyo%20con%20la%20plataforma."
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sidebar-whatsapp-support"
          className="group flex items-center gap-3 rounded-xl border border-[#25D366]/25 bg-[#F2FBF5] hover:bg-[#E4F7EB] px-3.5 py-2.5 transition-colors"
        >
          <span className="grid place-items-center h-9 w-9 shrink-0 rounded-full bg-[#25D366] text-white shadow-sm">
            <MessageCircle size={16} strokeWidth={2.2} />
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="text-[10px] tracking-[0.2em] uppercase text-forest font-medium">
              Soporte técnico
            </p>
            <p className="text-[13px] font-medium text-ink truncate">
              WhatsApp 33 4280 2102
            </p>
          </div>
          <ArrowRight
            size={13}
            className="text-ink-soft group-hover:text-forest transition-transform group-hover:translate-x-0.5 shrink-0"
          />
        </a>
      </div>

      {/* User */}
      <div className="px-3 pb-4 border-t border-rule pt-3">
        <div className="flex items-center gap-3 px-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="h-9 w-9 rounded-full object-cover"
            />
          ) : (
            <div className="h-9 w-9 rounded-full bg-forest text-paper grid place-items-center font-serif text-sm">
              {user?.name?.[0]?.toUpperCase() || "T"}
            </div>
          )}
          <div className="min-w-0 flex-1 leading-tight">
            <p
              data-testid="sidebar-user-name"
              className="text-[13px] font-medium text-ink truncate"
            >
              {user?.name}
            </p>
            <p className="text-[11px] text-ink-soft truncate">{user?.email}</p>
          </div>
          <button
            onClick={async () => {
              await logout();
              window.location.href = "/";
            }}
            data-testid="sidebar-logout-btn"
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-white text-ink-soft hover:text-ink transition-colors"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
}
